/**
 * CmdKPalette — MS461 catering-pattern rewrite.
 *
 * Matches lembra-catering.com CommandPalette exactly, adapted for EC:
 *   • Light PAPER background (not dark gradient)
 *   • Input at top with italic Newsreader placeholder
 *   • Tab row below input: "Search" | "Ask AI"
 *   • Ask AI: inline answer view with ThinkingStrip
 *   • Search: fuzzy-filtered internal destinations
 *   • Unified keyboard handler on dialog div
 *   • Two-stage Esc: dismiss answer → close
 *
 * Opens via ⌘K / Ctrl-K or CmdKNavButton in nav.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Cake,
  ChevronRight,
  CornerDownLeft,
  CreditCard,
  Flower2,
  Gift,
  Handshake,
  HelpCircle,
  Home,
  LogIn,
  MapPin,
  MessageCircle,
  Package,
  Receipt,
  Utensils,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ThinkingStrip, LEMBRA_VERB_VOCAB } from "@/components/lembra/ThinkingStrip";
// MS514: LembraMark replaced by inline LembraFaviconIcon (navy disc +
// butter italic 'l') to match the catering CommandPalette exactly.
import { supabase } from "@/integrations/supabase/client";

// ─── CmdK context (provider + hook) ──────────────────────────────────────────
interface CmdKContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

const CmdKCtx = createContext<CmdKContextValue | null>(null);

export function CmdKProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        const active = document.activeElement;
        const isTyping =
          active instanceof HTMLInputElement ||
          active instanceof HTMLTextAreaElement ||
          (active instanceof HTMLElement && active.isContentEditable);
        if (isTyping) return;
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <CmdKCtx.Provider value={{ isOpen, open, close }}>
      {children}
      {/* MS493: mount the palette itself inside the provider so it can
       *  actually appear when isOpen flips true. The MS461 catering-
       *  pattern PR split CmdKProvider + CmdKPalette into two exports
       *  but never wired the palette into the tree — clicking the nav
       *  button or pressing ⌘K toggled context state with no visible
       *  effect because no component was consuming isOpen. */}
      <CmdKPalette />
    </CmdKCtx.Provider>
  );
}

export function useCmdK(): CmdKContextValue {
  const ctx = useContext(CmdKCtx);
  if (!ctx) throw new Error("useCmdK must be used inside CmdKProvider");
  return ctx;
}

// ─── Color tokens (matching catering exactly) ────────────────────────────────
const NAVY = "#1D3461";
const NAVY_50 = "rgba(29,52,97,0.5)";
const NAVY_70 = "rgba(29,52,97,0.7)";
const NAVY_LINE = "rgba(29,52,97,0.14)";
const BUTTER = "#F7E8A4";
const PAPER = "#FAF7F0";
const CORAL = "#E05C40";
// MS514: Ask AI tab container now uses navy tints to match the
// catering CommandPalette exactly. Previous sage-wash (#498/#510)
// was the EC-specific interpretation; catering uses a lighter
// rgba(29,52,97,0.05) background + rgba(29,52,97,0.16) border —
// same NAVY token, lower opacity, no colour cast.
const ASK_CONTAINER_BG = "rgba(29,52,97,0.05)";
const ASK_CONTAINER_BORDER = "rgba(29,52,97,0.16)";

// ─── Types ────────────────────────────────────────────────────────────────────
type Tab = "search" | "ask";

interface AskAnswer {
  question: string;
  body: string;
  escalate: boolean;
  errored: boolean;
}

interface NavResult {
  icon: LucideIcon;
  title: string;
  subtitle: string;
  href: string;
}

interface AskResult {
  text: string;
  isPrimary: boolean;
  icon: LucideIcon;
}

// ─── Static data ─────────────────────────────────────────────────────────────
// MS495: replaced the MS461 catering-pattern PR's hardcoded list, which
// pointed at routes copy-pasted from the catering platform that DO NOT
// EXIST in EC's App.tsx (/send, /packages, /how-it-works, /occasions,
// /business all 404). Every Search result was a dead link. New list is
// the actual public routes EC's React Router resolves, double-checked
// against src/App.tsx.
// MS496: Send a gift now points at /chat (the inline concierge), which
// is where every OTHER Send-a-gift CTA on the platform routes via
// chatHref() — landing nav, mobile menu, FinalCTA, HowLembraWorks,
// vendor profile pages. The MS495 entry sent users into the legacy
// /order builder (Sprint 2.5a deprecated the path); App.tsx now
// redirects /order → /chat as a backstop.
const NAV_RESULTS: NavResult[] = [
  { icon: Home,       title: "Home",            subtitle: "Landing page",                              href: "/" },
  { icon: Gift,       title: "Send a gift",     subtitle: "Brief the concierge in chat",              href: "/chat" },
  { icon: Handshake,  title: "Browse vendors",  subtitle: "Local Melbourne makers we work with",       href: "/vendors" },
  { icon: CreditCard, title: "Pricing",         subtitle: "$99 flat, no subscriptions",                href: "/pricing" },
  { icon: Cake,       title: "Calculator",      subtitle: "Estimate the cost of your celebrations",    href: "/calculator" },
  { icon: Utensils,   title: "Catering",        subtitle: "Event & office catering",                   href: "/catering" },
  { icon: Handshake,  title: "Partner with us", subtitle: "Apply to join the vendor network",         href: "/partner-apply" },
  { icon: Flower2,    title: "Blog",            subtitle: "Stories, guides, and updates",              href: "/blog" },
  { icon: Package,    title: "About",           subtitle: "Why Lembra exists + our team",              href: "/about" },
  { icon: Package,    title: "Security",        subtitle: "How we keep your data safe",                href: "/security" },
  { icon: Package,    title: "Contact",         subtitle: "Get in touch with the team",                href: "/contact" },
  { icon: LogIn,      title: "Sign in",         subtitle: "Access your dashboard",                     href: "/login" },
];

// MS500: example prompts now carry context-matched icons so each row
// reads as a recognisable category of question (per Lucky's Heidi
// reference, where the "Explore X" cards use meaningful glyphs per
// product surface). Generic Sparkles repeated across all four rows
// made the list feel boilerplate.
const ASK_EXAMPLES: { text: string; icon: LucideIcon }[] = [
  { text: "How does Lembra work?",       icon: HelpCircle },
  { text: "Do you deliver to Sydney?",   icon: MapPin },
  { text: "What's included in $99?",     icon: Receipt },
  { text: "What gift types can I send?", icon: Gift },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

// MS500: TabButton's icon dropped per Lucky's "remove the search
// icon and star icons completely." Pills are pure text now — Search
// pill is just "Search," Ask AI pill is just "Ask AI." Active state
// (butter background) carries enough weight to differentiate without
// the leading glyph.
function TabButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "5px 14px",
        borderRadius: "9999px",
        fontSize: "12px",
        fontWeight: 500,
        fontFamily: "DM Sans, sans-serif",
        letterSpacing: "0.04em",
        color: active ? NAVY : `${NAVY}99`,
        background: active ? BUTTER : "transparent",
        border: "none",
        cursor: "pointer",
        transition: "background 0.15s, color 0.15s",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </button>
  );
}

function ResultRow({
  icon: Icon,
  title,
  subtitle,
  selected,
  primary,
  onMouseEnter,
  onClick,
}: {
  icon: LucideIcon;
  title: string;
  subtitle: string;
  selected: boolean;
  primary?: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
}) {
  return (
    <li role="option" aria-selected={selected}>
      <button
        type="button"
        onMouseEnter={onMouseEnter}
        onClick={onClick}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "10px 16px",
          background: selected ? BUTTER : "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          transition: "background 0.1s",
        }}
      >
        <span
          style={{
            flex: "0 0 32px",
            height: "32px",
            borderRadius: "8px",
            background: primary ? `${NAVY}0d` : `${NAVY}08`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon size={15} strokeWidth={1.8} color={NAVY} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            style={{
              display: "block",
              fontSize: "13.5px",
              fontWeight: primary ? 600 : 500,
              color: NAVY,
              fontFamily: "DM Sans, sans-serif",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title}
          </span>
          <span
            style={{
              display: "block",
              fontSize: "11.5px",
              color: `${NAVY}80`,
              fontFamily: "DM Sans, sans-serif",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {subtitle}
          </span>
        </span>
        <span style={{ flex: "0 0 auto", color: selected ? NAVY : `${NAVY}40` }}>
          {primary ? (
            <CornerDownLeft size={14} strokeWidth={1.8} />
          ) : selected ? (
            <ChevronRight size={14} strokeWidth={1.8} />
          ) : null}
        </span>
      </button>
    </li>
  );
}

function AskResultRow({
  text,
  icon: Icon,
  primary,
  selected,
  onMouseEnter,
  onClick,
}: {
  text: string;
  icon: LucideIcon;
  primary: boolean;
  selected: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
}) {
  return (
    <li role="option" aria-selected={selected}>
      <button
        type="button"
        onMouseEnter={onMouseEnter}
        onClick={onClick}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "10px 16px",
          background: selected ? BUTTER : "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          transition: "background 0.1s",
        }}
      >
        {/* MS503: icon badge + text now match the Search-tab card
            spec exactly — 32×32 rounded square with NAVY08 / NAVY0d
            wash, 15px icon, 13.5px DM Sans weight-500 full-NAVY
            label (was 13px italic Newsreader at 60% opacity, which
            Lucky correctly flagged as too small + too faded vs the
            Search tab rows). Primary "Ask Lembra: ..." keeps weight
            600 + the trailing CornerDownLeft to read as the
            submit-this-question action; everything else looks like
            a regular nav row. */}
        <span
          style={{
            flex: "0 0 32px",
            height: "32px",
            borderRadius: "8px",
            background: primary ? `${NAVY}0d` : `${NAVY}08`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon size={15} strokeWidth={1.8} color={NAVY} />
        </span>
        <span
          style={{
            flex: 1,
            fontSize: "13.5px",
            fontWeight: primary ? 600 : 500,
            color: NAVY,
            fontFamily: "DM Sans, sans-serif",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {text}
        </span>
        <span style={{ flex: "0 0 auto", color: selected ? NAVY : `${NAVY}40` }}>
          {primary ? (
            <CornerDownLeft size={14} strokeWidth={1.8} />
          ) : selected ? (
            <ChevronRight size={14} strokeWidth={1.8} />
          ) : null}
        </span>
      </button>
    </li>
  );
}

function AskThinkingView({ query }: { query: string }) {
  return (
    <div style={{ padding: "16px 16px 8px" }}>
      <div
        style={{
          background: `${NAVY}06`,
          borderRadius: "10px",
          padding: "14px 16px",
        }}
      >
        <p
          style={{
            fontSize: "13px",
            fontStyle: "italic",
            color: `${NAVY}80`,
            fontFamily: "'Newsreader', Georgia, serif",
            marginBottom: "12px",
          }}
        >
          "{query}"
        </p>
        {/* MS504: ThinkingStrip requires `active` + `verbs`. Pre-MS504
            we mounted it with no props — `active` was falsy and the
            component returned null, so users saw a blank loading state
            with the italic "{query}" line but no animated indicator.
            ambientAnswer is the design-canonical 4-verb Sparkles pack
            reserved for the ⌘K Ask AI surface. */}
        <ThinkingStrip
          active
          verbs={LEMBRA_VERB_VOCAB.ambientAnswer}
          glyph="sparkles"
          density="compact"
        />
      </div>
    </div>
  );
}

// MS495: AskAnswerView rebuilt to match the catering platform's
// CommandPalette pattern verbatim — Sparkles + italic Newsreader
// headline + indented italic body, coral pill primary CTA + soft
// underline "ask another" tertiary. The previous design used a
// generic Lembra-AI eyebrow + body block, didn't match catering.
// concierge-quick-answer returns a single `answer` string; we split
// the first sentence as the headline and use the rest as the body
// so the catering structure renders cleanly with the existing API.
function AskAnswerView({
  answer,
  onContinueInChat,
  onReset,
}: {
  answer: AskAnswer;
  onContinueInChat: () => void;
  onReset: () => void;
}) {
  // First sentence becomes the headline; remainder = body. Falls
  // back to using the whole answer as headline when there's only
  // one sentence (or no terminator).
  const trimmed = answer.body.trim();
  const sentenceEnd = trimmed.search(/[.!?](\s|$)/);
  const headline = sentenceEnd > 0
    ? trimmed.slice(0, sentenceEnd + 1)
    : trimmed;
  const body = sentenceEnd > 0
    ? trimmed.slice(sentenceEnd + 1).trim()
    : "";

  return (
    <div style={{ padding: "16px" }}>
      <div
        className="rounded-lg p-4 sm:p-5"
        style={{
          background: "rgba(29,52,97,0.04)",
          border: `1px solid ${NAVY_LINE}`,
        }}
        role="status"
        aria-live="polite"
      >
        {/* Headline — italic Newsreader, no leading glyph.
            MS500: Sparkles removed per Lucky ("remove the search
            icon and star icons completely"). The italic
            Newsreader weight is enough signal that this is the
            concierge speaking — a star next to it was redundant
            and started reading as AI-generic boilerplate. */}
        <div style={{ display: "flex", alignItems: "flex-start" }}>
          <p
            className="italic"
            style={{
              color: NAVY,
              fontSize: "17px",
              fontWeight: 500,
              letterSpacing: "-0.005em",
              fontFamily: "'Newsreader', Georgia, serif",
              margin: 0,
              lineHeight: 1.3,
            }}
          >
            {headline}
          </p>
        </div>

        {/* Body — italic Newsreader, indented */}
        {body && (
          <p
            className="italic leading-relaxed"
            style={{
              marginTop: "12px",
              marginLeft: "24px",
              color: NAVY_70,
              fontSize: "13.5px",
              fontFamily: "'Newsreader', Georgia, serif",
              whiteSpace: "pre-wrap",
            }}
          >
            {body}
          </p>
        )}

        {/* Coral pill: escalate path */}
        {(answer.escalate || answer.errored) && (
          <div style={{ marginTop: "16px", marginLeft: "24px" }}>
            <button
              type="button"
              onClick={onContinueInChat}
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-medium text-white transition-colors"
              style={{ background: CORAL, fontFamily: "DM Sans, sans-serif" }}
            >
              Continue in chat →
            </button>
          </div>
        )}

        {/* Soft tertiary — "Ask another question" underline link */}
        <div style={{ marginTop: "16px", marginLeft: "24px" }}>
          <button
            type="button"
            onClick={onReset}
            className="text-[12px] underline underline-offset-4 transition-colors"
            style={{
              color: NAVY_50,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: 0,
              fontFamily: "DM Sans, sans-serif",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.color = NAVY;
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.color = NAVY_50;
            }}
          >
            Ask another question
          </button>
        </div>
      </div>
    </div>
  );
}

function PaletteEmptyState({ body }: { body: string }) {
  return (
    <div style={{ padding: "32px 16px", textAlign: "center" }}>
      <p
        style={{
          fontSize: "14px",
          fontStyle: "italic",
          color: `${NAVY}60`,
          fontFamily: "'Newsreader', Georgia, serif",
          marginBottom: "6px",
        }}
      >
        No matches
      </p>
      <p style={{ fontSize: "12px", color: `${NAVY}50`, fontFamily: "DM Sans, sans-serif" }}>
        {body}
      </p>
    </div>
  );
}

function KeyHint({ label }: { label: string }) {
  return (
    <kbd
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 6px",
        borderRadius: "4px",
        fontSize: "10px",
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        background: `${NAVY}0a`,
        color: `${NAVY}80`,
        border: `1px solid ${NAVY_LINE}`,
      }}
    >
      {label}
    </kbd>
  );
}

// MS514: navy disc with butter italic 'l' — matches catering
// CommandPalette's LembraFaviconIcon exactly. Replaces the
// LembraMark SVG in the Ask AI input row.
function LembraFaviconIcon() {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "20px",
        height: "20px",
        borderRadius: "999px",
        background: NAVY,
        flexShrink: 0,
        overflow: "hidden",
      }}
      aria-hidden="true"
    >
      <span
        style={{
          fontFamily: "'Newsreader', Georgia, serif",
          fontStyle: "italic",
          fontWeight: 600,
          color: BUTTER,
          fontSize: "72%",
          lineHeight: 1,
          display: "block",
          transform: "translate(-8%, 8%)",
          userSelect: "none",
        }}
      >
        l
      </span>
    </span>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export function CmdKPalette() {
  const { isOpen, close } = useCmdK();
  const navigate = useNavigate();

  const [tab, setTab] = useState<Tab>("search");
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [askLoading, setAskLoading] = useState<{ query: string } | null>(null);
  const [askAnswer, setAskAnswer] = useState<AskAnswer | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  const showingAnswer = !!askAnswer;
  const showingThinking = !!askLoading;

  // ── Derived state ──────────────────────────────────────────────────────────

  const searchResults = useMemo<NavResult[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return NAV_RESULTS;
    return NAV_RESULTS.filter(
      (r) =>
        r.title.toLowerCase().includes(q) ||
        r.subtitle.toLowerCase().includes(q) ||
        r.href.toLowerCase().includes(q),
    );
  }, [query]);

  const askResults = useMemo<AskResult[]>(() => {
    const q = query.trim();
    if (!q) return ASK_EXAMPLES.map((ex) => ({ text: ex.text, isPrimary: false, icon: ex.icon }));
    return [
      // MS500: primary "Ask Lembra" row uses MessageCircle — the row
      // IS the user's typed question being sent to chat, so a comms
      // glyph reads more honestly than a star or sparkle.
      { text: `Ask Lembra: "${q}"`, isPrimary: true, icon: MessageCircle },
      ...ASK_EXAMPLES.map((ex) => ({ text: ex.text, isPrimary: false, icon: ex.icon })),
    ];
  }, [query]);

  const currentResults = tab === "search" ? searchResults : askResults;
  const maxIndex = currentResults.length - 1;

  // ── Reset on open/close ────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setTab("search");
      setQuery("");
      setSelectedIndex(0);
      setAskLoading(null);
      setAskAnswer(null);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // ── Clamp selected index ───────────────────────────────────────────────────
  useEffect(() => {
    setSelectedIndex((i) => Math.min(i, maxIndex));
  }, [maxIndex]);

  // ── Ask AI ─────────────────────────────────────────────────────────────────
  // MS497: Lucky reported "Something went wrong" persisting in production
  // even though the concierge-quick-answer function returns 200 with real
  // answers (verified via logs + direct curl). The pre-MS497 catch block
  // was a bare `catch {}` that swallowed the actual error — we had no way
  // to know whether the failure was a CORS rejection, a JSON parse, a
  // FunctionsHttpError, a timeout, or something else.
  //
  // This refactor:
  //   1. catch (err) → console.error the full error shape (devtools)
  //   2. Direct fetch() fallback when supabase.functions.invoke fails.
  //      The fallback uses the same auth header + endpoint but bypasses
  //      whatever supabase-js v2 does internally. If invoke is the
  //      problem (CORS preflight handling, response-body parsing
  //      quirk), the fallback path will get through.
  //   3. Reports the exact failure stage in console so the next failed
  //      attempt surfaces the actual cause.
  const submitAsk = useCallback(async (rawQuery: string) => {
    const q = rawQuery.trim();
    // MS504: explicit startup log so when Lucky retests in prod and
    // reports "still nothing happens" we can tell from devtools whether
    // the handler is firing AT ALL vs. firing but failing downstream.
    // The pre-MS504 path had MS497's catch-block logging only — silent
    // if submitAsk itself never got called (e.g. row click bound to
    // wrong handler, Enter not reaching keydown).
    // eslint-disable-next-line no-console
    console.log("[CmdK Ask] submitAsk fired", { q, hasQuery: !!q });
    if (!q) return;
    setQuery(q);
    setAskLoading({ query: q });
    setAskAnswer(null);

    // Attempt 1: supabase.functions.invoke (preferred — handles auth
    // header injection + JSON parsing automatically).
    let answerText: string | null = null;
    let escalate = false;
    let stage = "invoke";

    try {
      const { data, error } = await supabase.functions.invoke("concierge-quick-answer", {
        body: { question: q },
      });
      if (error) throw error;
      if (data?.answer && typeof data.answer === "string") {
        answerText = data.answer;
        escalate = !!data?.escalate;
      } else {
        // data shape unexpected — log + fall through to fetch fallback
        console.warn("[CmdK Ask] invoke returned unexpected shape", { data });
        throw new Error("invoke_bad_shape");
      }
    } catch (invokeErr) {
      console.error("[CmdK Ask] invoke failed, trying direct fetch", invokeErr);
      stage = "fetch_fallback";
      let lastStatus: number | null = null;
      let lastServerError: string | null = null;
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "https://nbmpyagsjfheforuzwvf.supabase.co";
        const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
        const res = await fetch(`${supabaseUrl}/functions/v1/concierge-quick-answer`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(anonKey ? { Authorization: `Bearer ${anonKey}`, apikey: anonKey } : {}),
          },
          body: JSON.stringify({ question: q }),
        });
        lastStatus = res.status;
        if (!res.ok) {
          const errText = await res.text().catch(() => "<unreadable>");
          console.error("[CmdK Ask] direct fetch returned non-2xx", { status: res.status, body: errText.slice(0, 300) });
          // MS511: try to parse server's structured error so we can
          // surface it to the user instead of generic "Something
          // went wrong". 429 means rate-limited, 5xx means server
          // crash, etc. — wildly different things to a user.
          try {
            const parsed = JSON.parse(errText);
            lastServerError = typeof parsed?.error === "string" ? parsed.error : null;
          } catch {
            lastServerError = null;
          }
          throw new Error(`fetch_${res.status}`);
        }
        const json = await res.json();
        if (json?.answer && typeof json.answer === "string") {
          answerText = json.answer;
          escalate = !!json?.escalate;
        } else {
          console.error("[CmdK Ask] direct fetch shape bad", { json });
          throw new Error("fetch_bad_shape");
        }
      } catch (fetchErr) {
        console.error("[CmdK Ask] both invoke + fetch failed", { stage, fetchErr, lastStatus, lastServerError });
        // MS511: structured user-facing error. Rate-limit (429) and
        // server-down (5xx) deserve specific copy so the user knows
        // whether to wait, retry, or escalate to the team. Network-
        // level failures (Failed to fetch, no status) read as a
        // generic "couldn't reach us" — covers CORS, offline, etc.
        let body: string;
        if (lastStatus === 429) {
          body =
            lastServerError ??
            "You've asked a lot of questions recently. Give it a minute and try again.";
        } else if (lastStatus && lastStatus >= 500) {
          body =
            "Our concierge backend hiccupped (server error). Try again in a moment, or start a chat below.";
        } else if (lastStatus && lastStatus >= 400) {
          body =
            lastServerError ??
            `Something didn't go through (HTTP ${lastStatus}). Start a chat below and we'll pick it up there.`;
        } else {
          body =
            "Couldn't reach our concierge backend (check your connection). Try again, or start a chat below.";
        }
        setAskAnswer({
          question: q,
          body,
          escalate: true,
          errored: true,
        });
        setAskLoading(null);
        return;
      }
    }

    setAskAnswer({
      question: q,
      body: answerText ?? "I'm not sure. Let me connect you with the team.",
      escalate,
      errored: false,
    });
    setAskLoading(null);
  }, []);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleSelect = useCallback(
    (index: number) => {
      if (tab === "search") {
        const result = searchResults[index];
        if (!result) return;
        close();
        navigate(result.href);
      } else {
        const result = askResults[index];
        if (!result) return;
        const question = result.isPrimary ? query.trim() : result.text;
        void submitAsk(question);
      }
    },
    [tab, searchResults, askResults, query, close, navigate, submitAsk],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        if (showingAnswer || showingThinking) {
          setAskAnswer(null);
          setAskLoading(null);
          setQuery("");
          return;
        }
        close();
        return;
      }

      if (showingAnswer || showingThinking) {
        if (e.key === "Enter" && showingAnswer) {
          void submitAsk(query);
        }
        return;
      }

      if (e.key === "Tab") {
        e.preventDefault();
        setTab((t) => (t === "search" ? "ask" : "search"));
        setSelectedIndex(0);
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, maxIndex));
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
        return;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        handleSelect(selectedIndex);
        return;
      }
    },
    [showingAnswer, showingThinking, close, submitAsk, query, maxIndex, selectedIndex, handleSelect],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setSelectedIndex(0);
    setAskAnswer(null);
    setAskLoading(null);
  };

  const handleContinueInChat = () => {
    close();
    navigate("/#chat");
  };

  const handleReset = () => {
    setAskAnswer(null);
    setAskLoading(null);
    setQuery("");
    setTimeout(() => inputRef.current?.focus(), 30);
  };

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Search or Ask AI"
      onKeyDown={handleKeyDown}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "clamp(60px, 12vh, 120px)",
        paddingLeft: "16px",
        paddingRight: "16px",
      }}
    >
      {/* Backdrop */}
      <div
        aria-hidden="true"
        onClick={close}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(29,52,97,0.18)",
          backdropFilter: "blur(2px)",
        }}
      />

      {/* Panel */}
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "560px",
          background: PAPER,
          borderRadius: "16px",
          border: `1px solid ${NAVY_LINE}`,
          boxShadow: "0 24px 64px rgba(29,52,97,0.14), 0 4px 16px rgba(29,52,97,0.08)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* ── 1. Input row ── */}
        {/* MS514: Ask AI tab now matches the catering CommandPalette
            exactly — navy-tinted pill container (rgba 5% bg,
            rgba 16% border), LembraFaviconIcon leading mark, and
            a navy send button that appears when the buyer has typed
            something. Search tab keeps the existing flat row style. */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: tab === "ask" ? "10px 16px" : "14px 16px",
            borderBottom: `1px solid ${NAVY_LINE}`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flex: 1,
              padding: tab === "ask" ? "6px 12px" : 0,
              borderRadius: tab === "ask" ? "12px" : 0,
              background: tab === "ask" ? ASK_CONTAINER_BG : "transparent",
              border: tab === "ask" ? `1.5px solid ${ASK_CONTAINER_BORDER}` : "none",
              transition:
                "background 200ms ease, border-color 200ms ease, padding 200ms ease",
            }}
          >
            {tab === "ask" && <LembraFaviconIcon />}
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={handleInputChange}
              placeholder={
                tab === "search"
                  ? "Search Lembra…"
                  : "Ask anything about Lembra…"
              }
              className="cmdk-input"
              style={{
                flex: 1,
                border: "none",
                outline: "none",
                background: "transparent",
                fontSize: "15px",
                fontStyle: "normal",
                fontFamily: "DM Sans, sans-serif",
                color: NAVY,
                caretColor: NAVY,
              }}
              autoComplete="off"
              spellCheck={false}
            />
            <style>{`
              .cmdk-input::placeholder {
                font-style: italic;
                font-family: 'Newsreader', Georgia, serif;
                color: rgba(29, 52, 97, 0.5);
              }
            `}</style>
            {/* Send button — only visible on Ask AI tab when the
                buyer has typed something. Matches catering pattern:
                navy circle, CornerDownLeft icon, submits in-modal. */}
            {tab === "ask" && query.trim().length > 0 && (
              <button
                type="button"
                onClick={() => void submitAsk(query)}
                aria-label="Send question"
                style={{
                  display: "flex",
                  height: "28px",
                  width: "28px",
                  flexShrink: 0,
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "8px",
                  border: "none",
                  background: NAVY,
                  cursor: "pointer",
                }}
              >
                <CornerDownLeft size={14} strokeWidth={2} color="white" />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            style={{
              flexShrink: 0,
              padding: "4px",
              borderRadius: "6px",
              border: "none",
              background: "transparent",
              color: `${NAVY}60`,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <X size={16} strokeWidth={2} />
          </button>
        </div>

        {/* ── 2. Tab row ── */}
        <div
          role="tablist"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            padding: "8px 12px",
            borderBottom: `1px solid ${NAVY_LINE}`,
          }}
        >
          <TabButton
            label="Search"
            active={tab === "search"}
            onClick={() => { setTab("search"); setSelectedIndex(0); inputRef.current?.focus(); }}
          />
          <TabButton
            label="Ask AI"
            active={tab === "ask"}
            onClick={() => { setTab("ask"); setSelectedIndex(0); inputRef.current?.focus(); }}
          />
          <span
            style={{
              marginLeft: "auto",
              fontSize: "11px",
              color: `${NAVY}50`,
              fontFamily: "DM Sans, sans-serif",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <KeyHint label="Tab" />
            <span>to switch</span>
          </span>
        </div>

        {/* ── 3. Results body ── */}
        <div style={{ maxHeight: "55vh", overflowY: "auto" }}>
          {/* Thinking state */}
          {showingThinking && <AskThinkingView query={askLoading!.query} />}

          {/* Answer state */}
          {showingAnswer && !showingThinking && (
            <AskAnswerView
              answer={askAnswer!}
              onContinueInChat={handleContinueInChat}
              onReset={handleReset}
            />
          )}

          {/* Normal results */}
          {!showingThinking && !showingAnswer && (
            <>
              {tab === "search" && (
                <>
                  {searchResults.length > 0 ? (
                    <ul
                      role="listbox"
                      aria-label="Search results"
                      style={{ listStyle: "none", margin: 0, padding: "6px 0" }}
                    >
                      {searchResults.map((result, i) => (
                        <ResultRow
                          key={result.href}
                          icon={result.icon}
                          title={result.title}
                          subtitle={result.subtitle}
                          selected={i === selectedIndex}
                          onMouseEnter={() => setSelectedIndex(i)}
                          onClick={() => handleSelect(i)}
                        />
                      ))}
                    </ul>
                  ) : (
                    <PaletteEmptyState body={`No pages match "${query}"`} />
                  )}
                </>
              )}

              {tab === "ask" && (
                <ul
                  role="listbox"
                  aria-label="Ask AI suggestions"
                  style={{ listStyle: "none", margin: 0, padding: "6px 0" }}
                >
                  {askResults.map((result, i) => (
                    <AskResultRow
                      key={result.text}
                      text={result.text}
                      icon={result.icon}
                      primary={result.isPrimary}
                      selected={i === selectedIndex}
                      onMouseEnter={() => setSelectedIndex(i)}
                      onClick={() => handleSelect(i)}
                    />
                  ))}
                </ul>
              )}
            </>
          )}
        </div>

        {/* ── 4. Footer ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "10px 16px",
            borderTop: `1px solid ${NAVY_LINE}`,
            flexWrap: "wrap",
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: `${NAVY}60`, fontFamily: "DM Sans, sans-serif" }}>
            <KeyHint label="↑↓" /> navigate
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: `${NAVY}60`, fontFamily: "DM Sans, sans-serif" }}>
            <KeyHint label="↵" /> select
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: `${NAVY}60`, fontFamily: "DM Sans, sans-serif" }}>
            <KeyHint label="Esc" /> close
          </span>
          <span style={{ marginLeft: "auto" }}>
            <button
              type="button"
              onClick={handleContinueInChat}
              style={{
                fontSize: "11px",
                color: `${NAVY}70`,
                fontFamily: "DM Sans, sans-serif",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: 0,
                textDecoration: "underline",
                textUnderlineOffset: "2px",
              }}
            >
              Open chat →
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}
